import React, { useRef, useState } from "react";
import { PrinterOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useReactToPrint } from "react-to-print";
import { API } from "api";
import { UTILS } from "utils";
import AppModal from "components/ui/AppModal";
import AppButton from "components/ui/AppButton";
import AppOutlineButton from "components/ui/AppOutlineButton";
import RefundForm from "../../../components/RefundOrderForm";
import RefundNotRequiredConfirmation from "./RefundNotRequiredConfirmation";
import OrderDetailUnifiedBody from "./OrderDetailUnifiedBody";
import SelectedIntegrations from "../../../logistic-integrations/SelectedLogistics";
import { CONSTANTS as C } from "config";

const OrderDetailsModal = ({
    open,
    onClose,
    description = null,
    tabStatus,
    setRejectReason,
    onSubmit,
    getOrdersByStatus,
    refundableMutate,
    setLoading,
    getOrderInfoById,
}) => {
    const { t } = useTranslation();
    const order = description;

    const [refundNotRequiredInfo, setRefundNotRequiredInfo] = useState({
        loading: false,
        visible: false,
    });
    const [refundForm, setRefundForm] = useState({
        loading: false,
        visible: false,
    });

    const printContentRef = useRef(null);
    const handlePrint = useReactToPrint({
        content: () => printContentRef.current,
    });

    if (!order || Object.keys(order).length === 0) {
        return null;
    }

    const runSubmit = async (status, type) => {
        const ok = await onSubmit?.(status, type);
        if (ok) onClose?.();
    };

    const openRejectModal = (rejectType) => {
        setRejectReason?.((prevState) => ({
            ...prevState,
            values: {
                ...prevState.values,
                Modal: true,
                type: rejectType,
            },
        }));
    };

    const setOrderNotRefundable = async () => {
        setRefundNotRequiredInfo((s) => ({ ...s, loading: true }));
        const obj = {
            orderId: order?._id,
            brandId: order?.brandId,
        };
        const result = await API.SET_ORDER_NOT_REFUNDABLE(obj);
        if (result?.success === 1) UTILS.successNotification(t("Order Updated!"));
        else UTILS.errorNotification(t("Please try again later!"));
        await getOrdersByStatus?.();
        setRefundNotRequiredInfo({ loading: false, visible: false });
        refundableMutate?.("order/get-refundable-orders-count", {
            revalidate: true,
        });
        onClose?.();
    };

    const submitRefundOrder = async ({ amount, reason }) => {
        setRefundForm((s) => ({ ...s, loading: true }));
        const obj = {
            orderId: order?._id,
            amount,
            reason,
            brandId: order?.brandId,
        };
        const result = await API.REFUND_ORDER(obj);
        if (result?.success === 1)
            UTILS.successNotification(result?.message || t("Refund Requested"));
        else
            UTILS.errorNotification(result?.message || t("Please try again Later"));
        await getOrdersByStatus?.();
        setLoading?.(false);
        setRefundForm({ loading: false, visible: false });
        refundableMutate?.("order/get-refundable-orders-count", {
            revalidate: true,
        });
        onClose?.();
    };

    const getStatusText = () => {
        if (!order?.orderStatus) return "N/A";
        const status = order.orderStatus.replaceAll("_", " ");
        return status
            .toLowerCase()
            .split(" ")
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(" ");
    };

    const getStatusBadgeStyle = () => {
        const rawStatus = String(order?.orderStatus || "");
        const normalizedStatus = rawStatus
            .toUpperCase()
            .replaceAll(" ", "_")
            .replaceAll("/", "_");

        const statusStyles = {
            PENDING: { backgroundColor: "#FFF9E6", color: "#FF8C00" },
            PREPARING: { backgroundColor: "#FFF4E6", color: "#F97316" },
            ON_THE_WAY: { backgroundColor: "#E6F2FF", color: "#3B82F6" },
            READY: { backgroundColor: "#E6F2FF", color: "#3B82F6" },
            COMPLETE: { backgroundColor: "#E6F7E6", color: "#22C55E" },
            COMPLETED: { backgroundColor: "#E6F7E6", color: "#22C55E" },
            SCHEDULED: { backgroundColor: "#E6F7E6", color: "#22C55E" },
            REJECTED: { backgroundColor: "#FFE6E6", color: "#EF4444" },
            CANCELLED: { backgroundColor: "#FFE6E6", color: "#EF4444" },
            CANCELED: { backgroundColor: "#FFE6E6", color: "#EF4444" },
            REFUND: { backgroundColor: "#FDECEC", color: "#D4380D" },
            REFUNDED: { backgroundColor: "#FDECEC", color: "#D4380D" },
        };

        return (
            statusStyles[normalizedStatus] || {
                backgroundColor: "#F3F4F6",
                color: "#4B5563",
            }
        );
    };

    const modalTitle = (
        <div className="flex items-center justify-center gap-3">
            <span className="font-semibold text-gray-900">
                {t("Order")} # {order?.orderNumber || "N/A"}
            </span>
            {order?.orderStatus && (
                <span
                    className="rounded-full px-3 py-1 text-sm font-medium"
                    style={getStatusBadgeStyle()}
                >
                    {getStatusText()}
                </span>
            )}
        </div>
    );

    const closeRefundModals = () => {
        setRefundNotRequiredInfo((s) => ({ ...s, loading: false, visible: false }));
        setRefundForm((s) => ({ ...s, loading: false, visible: false }));
    };

    const normalizeOrderFlowStatus = (value) =>
        String(value || "")
            .toUpperCase()
            .replaceAll(" ", "_")
            .replaceAll("/", "_");

    /** Map list tab to footer actions (IN_PROGRESS and ALL can contain mixed statuses). */
    const footerTabStatus = (() => {
        const tab = normalizeOrderFlowStatus(tabStatus);
        const orderSt = normalizeOrderFlowStatus(order?.orderStatus);

        if (tab === "ALL") {
            if (orderSt === "READY") return "ON_THE_WAY";
            return orderSt || null;
        }

        if (tab === "IN_PROGRESS") {
            if (orderSt === "ON_THE_WAY" || orderSt === "READY") {
                return "ON_THE_WAY";
            }
            if (orderSt === "PREPARING" || orderSt === "IN_PROGRESS") {
                return "PREPARING";
            }
            return null;
        }
        return tabStatus;
    })();

    const modalFooter = (() => {
        const printButton = (
            <AppButton
                type="button"
                variant="primaryOutline"
                size="sm"
                className="hidden-print"
                icon={<PrinterOutlined />}
                onClick={() => handlePrint()}
            >
                {t("Print")}
            </AppButton>
        );

        const printOnlyFooter = (
            <div className="flex w-full items-center justify-start">
                {printButton}
            </div>
        );

        const footerRow = (outlineChild, primaryChild) => (
            <div className="flex w-full flex-wrap items-center justify-between gap-3">
                <div className="flex flex-shrink-0">{printButton}</div>
                <div className="flex min-w-0 flex-1 flex-wrap justify-end gap-2">
                    {outlineChild}
                    {primaryChild}
                </div>
            </div>
        );

        if (!footerTabStatus || !onSubmit) {
            return printOnlyFooter;
        }

        if (normalizeOrderFlowStatus(footerTabStatus) === "PENDING") {
            return footerRow(
                <AppOutlineButton
                    variant="secondary"
                    onClick={() => openRejectModal("REJECTED")}
                >
                    {t("Reject")}
                </AppOutlineButton>,
                <AppButton
                    variant="primary"
                    onClick={() =>
                        runSubmit(
                            order?.isPreOrder ? "SCHEDULED" : "PREPARING",
                            null
                        )
                    }
                >
                    {order?.isPreOrder ? t("Schedule") : t("Accept Order")}
                </AppButton>
            );
        }
        if (normalizeOrderFlowStatus(footerTabStatus) === "PREPARING") {
            return footerRow(
                <AppOutlineButton
                    variant="secondary"
                    onClick={() => openRejectModal("CANCELLED")}
                >
                    {t("Cancel")}
                </AppOutlineButton>,
                <AppButton
                    variant="primary"
                    onClick={() => runSubmit("ON_THE_WAY", null)}
                >
                    {t("On The Way / Ready")}
                </AppButton>
            );
        }
        if (normalizeOrderFlowStatus(footerTabStatus) === "ON_THE_WAY") {
            return footerRow(
                <AppOutlineButton
                    variant="secondary"
                    onClick={() => openRejectModal("CANCELLED")}
                >
                    {t("Cancel")}
                </AppOutlineButton>,
                <AppButton
                    variant="primary"
                    onClick={() => runSubmit("COMPLETE", null)}
                >
                    {t("Mark as COMPLETE")}
                </AppButton>
            );
        }
        if (normalizeOrderFlowStatus(footerTabStatus) === "SCHEDULED") {
            return footerRow(
                <AppOutlineButton
                    variant="secondary"
                    onClick={() => openRejectModal("REJECTED")}
                >
                    {t("Reject")}
                </AppOutlineButton>,
                <AppButton
                    variant="primary"
                    onClick={() => runSubmit("PREPARING", null)}
                >
                    {t("Start Preparing")}
                </AppButton>
            );
        }
        if (normalizeOrderFlowStatus(footerTabStatus) === "REFUND" && refundableMutate) {
            return footerRow(
                <AppOutlineButton
                    variant="secondary"
                    onClick={() =>
                        setRefundNotRequiredInfo((s) => ({
                            ...s,
                            visible: true,
                        }))
                    }
                >
                    {t("No need for refund")}
                </AppOutlineButton>,
                <AppButton
                    variant="danger"
                    onClick={() =>
                        setRefundForm({ visible: true, loading: false })
                    }
                >
                    {t("Refund")}
                </AppButton>
            );
        }
        return printOnlyFooter;
    })();

    return (
        <>
            <AppModal
                open={open}
                onClose={onClose}
                className="app-modal app-modal-order-details"
                titleAlign="start"
                title={modalTitle}
                footer={modalFooter}
                bodyStyle={{ paddingRight: "8px" }}
            >
                <OrderDetailUnifiedBody
                    order={order}
                    orderId={order._id}
                    tabStatus={tabStatus}
                    getOrderInfoById={getOrderInfoById}
                    showToolbar={false}
                    showDeliveryActions={false}
                    printContentRef={printContentRef}
                />
                {order?.orderType?.type === C.DELIVERY_SERICETYPE ? (
                    <div className="mt-4 pt-3">
                        <div className="hidden-print flex flex-wrap">
                            <SelectedIntegrations order={order} />
                        </div>
                    </div>
                ) : null}
            </AppModal>
            <AppModal
                open={refundNotRequiredInfo.visible || refundForm.visible}
                onClose={closeRefundModals}
                width={520}
                title={
                    refundNotRequiredInfo.visible
                        ? t("No need for refund")
                        : t("Refund")
                }
            >
                {refundNotRequiredInfo.visible && (
                    <RefundNotRequiredConfirmation
                        loading={refundNotRequiredInfo?.loading}
                        order={order}
                        onSubmit={setOrderNotRefundable}
                        closeModal={() => {
                            setRefundNotRequiredInfo({
                                loading: false,
                                visible: false,
                            });
                        }}
                    />
                )}
                {refundForm.visible && (
                    <RefundForm
                        loading={refundForm?.loading}
                        order={order}
                        onSubmit={submitRefundOrder}
                        closeModal={() => {
                            setRefundForm({
                                loading: false,
                                visible: false,
                            });
                        }}
                    />
                )}
            </AppModal>
        </>
    );
};

export default OrderDetailsModal;
